'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import { Link } from '@/i18n/navigation'
import { useLocale } from 'next-intl'
import { fetchAllNews, type NewsItem } from '@/services/news'
import { t } from '@/i18n/translations'
// Eşleme paylaşımlı modülde: haber sayfasının SUNUCU bileşeni de aynı çeviriyi
// kullanıyor ve 'use client' modülünden fonksiyon çağıramaz.
import { categoryKeys, categoryColorMap, urlSlugToApiKey, apiKeyToUrlSlug, categorySlugFor } from '@/data/newsCategories'


function formatCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(n)
}

function formatRelativeTime(iso: string, lang: string): string {
  const now = Date.now()
  const diff = now - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return t('time.justNow', lang)
  if (mins < 60) return `${mins} ${t('time.minsAgo', lang)}`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} ${t('time.hoursAgo', lang)}`
  const days = Math.floor(hours / 24)
  return `${days} ${t('time.daysAgo', lang)}`
}

function NewsCard({ item, lang }: { item: NewsItem; lang: string }) {
  const catColor = categoryColorMap[item.category] ?? '#666'
  const cat = categoryKeys.find(c => c.key === item.category)
  const catLabel = cat ? t(cat.i18n, lang) : item.category

  return (
    <Link
      href={{ pathname: '/news/[slug]', params: { slug: item.slug } }}
      className="block rounded-xl border no-underline transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
      }}
    >
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ backgroundColor: catColor + '18', color: catColor }}
          >
            {catLabel}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
            {item.source}
          </span>
        </div>

        {/* SG2 (Ağustos 2026 denetimi): haber kartları h3 ile işaretliydi, bu yüzden
            /haberler ve /news sayfalarında hiç h2 yoktu; aynı mantıkta çalışan
            /rehber sayfası ise kart başlıklarını h2 taşıyordu. İki listeleme
            sayfası artık aynı başlık hiyerarşisini kullanıyor. */}
        <h2
          className="text-[14px] font-bold leading-snug m-0 mb-2 line-clamp-2"
          style={{ color: 'var(--text-main)' }}
        >
          {item.title}
        </h2>

        <p
          className="text-[12px] leading-relaxed m-0 mb-3 line-clamp-2"
          style={{ color: 'var(--text-muted)' }}
        >
          {item.summary}
        </p>

        <div className="flex items-center gap-2">
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {item.source}
          </span>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>·</span>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {formatRelativeTime(item.publishedAt, lang)}
          </span>
          <div className="flex items-center gap-2.5 ml-auto" style={{ color: 'var(--text-muted)' }}>
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <span className="text-[10px] tabular-nums">{formatCount(item.views)}</span>
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
              <span className="text-[10px] tabular-nums">{formatCount(item.likes)}</span>
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
              <span className="text-[10px] tabular-nums">{formatCount(item.dislikes)}</span>
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              <span className="text-[10px] tabular-nums">{formatCount(item.commentCount)}</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}

function SkeletonCard() {
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
    >
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className="animate-pulse rounded-full h-4 w-16" style={{ backgroundColor: 'var(--border-color)' }} />
          <div className="animate-pulse rounded h-4 w-12" style={{ backgroundColor: 'var(--border-color)' }} />
        </div>
        <div className="animate-pulse rounded h-5 w-full" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="animate-pulse rounded h-5 w-3/4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="animate-pulse rounded h-4 w-full" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="animate-pulse rounded h-4 w-2/3" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex gap-2">
          <div className="animate-pulse rounded h-3 w-14" style={{ backgroundColor: 'var(--border-color)' }} />
          <div className="animate-pulse rounded h-3 w-16" style={{ backgroundColor: 'var(--border-color)' }} />
        </div>
      </div>
    </div>
  )
}

/**
 * SG2 (Ağustos 2026 denetimi): /haberler ve /news sayfalarında hiç h2 yoktu.
 * Kart başlıklarını h2'ye çevirmek tek başına yetmedi — liste tamamen istemcide
 * çekildiği için ilk HTML yanıtında hiçbir kart yoktu. Sunucu bileşeni haberleri
 * zaten JSON-LD için çekiyordu; artık aynı veri ilk render'a da tohumlanıyor.
 * Böylece kart başlıkları ilk HTML'de yer alır ve tarayıcı ek istek atmadan gösterir.
 */
export default function NewsListClient({
  initialNews = [],
  initialTotalPages = 1,
  initialTotalCount = 0,
  category: categoryFromRoute,
}: {
  initialNews?: NewsItem[]
  /**
   * Sunucudaki listenin toplam sayfa/kayıt sayısı. Tohum kullanıldığında ilk fetch
   * atlandığı için bu değerler geçilmezse sayfalama kontrolü `totalPages === 1`de
   * kalıyor ve HİÇ görünmüyordu (ilk açılışta 2. sayfaya geçmek imkânsızdı).
   */
  initialTotalPages?: number
  initialTotalCount?: number
  /**
   * Kategori sayfasından gelen API anahtarı. Verildiğinde kategori ROTANIN parçasıdır;
   * query string'e YAZILMAZ — yoksa /haberler/kategori/ekonomi?kategori=ekonomi gibi
   * ikinci bir adres doğar ve canonical'ı bozar.
   */
  category?: string
}) {
  const lang = useLocale()
  const searchParams = useSearchParams()
  const navRef = useRef<HTMLElement>(null)

  const catParam = lang === 'en' ? 'category' : 'kategori'
  const pageParam = lang === 'en' ? 'page' : 'sayfa'
  const searchParam = lang === 'en' ? 'q' : 'arama'

  const rawCatSlug = searchParams.get(catParam) ?? searchParams.get(lang === 'en' ? 'kategori' : 'category') ?? ''
  const currentCategory = categoryFromRoute ?? urlSlugToApiKey(rawCatSlug)
  const currentPage = parseInt(searchParams.get(pageParam) ?? searchParams.get(lang === 'en' ? 'sayfa' : 'page') ?? '1', 10)
  const currentSearch = searchParams.get(searchParam) ?? searchParams.get(lang === 'en' ? 'arama' : 'q') ?? ''

  // Sunucu bileşeni İSTENEN sayfa/kategori/aramayı çekip geçirir; ilk render'da
  // sunucu ile istemci aynı searchParams'ı okuduğu için tohum her zaman doğru
  // listeye karşılık gelir. Sonraki filtre değişimleri normal fetch yoluyla gider.
  const seedApplies = initialNews.length > 0
  const [news, setNews] = useState<NewsItem[]>(seedApplies ? initialNews : [])
  const [totalPages, setTotalPages] = useState(seedApplies ? initialTotalPages : 1)
  const [totalCount, setTotalCount] = useState(seedApplies ? initialTotalCount : 0)
  const [loading, setLoading] = useState(!seedApplies)
  // Tohum kullanıldıysa ilk fetch atlanır; sonraki filtre/sayfa değişimlerinde çalışır.
  const seedConsumed = useRef(seedApplies)
  const [searchInput, setSearchInput] = useState(currentSearch)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)

  /**
   * Sayfa/arama artık DURUMDA tutuluyor, adres çubuğundan geri okunmuyor.
   * Sayfa değişimi `window.history.replaceState` ile yazılıyor; `useSearchParams()`
   * bunu her zaman aynı render'da yansıtmıyor, o durumda "2"ye basınca yeniden
   * 1. sayfa çekiliyordu. URL yine güncelleniyor (canonical/paylaşım için), ama
   * hangi verinin çekileceğine durum karar veriyor.
   */
  const [page, setPageState] = useState(currentPage)
  const [search, setSearch] = useState(currentSearch)

  // Gerçek gezinme (kategori bağlantısı, geri tuşu, dışarıdan gelen link) URL'i
  // değiştirdiğinde durum ona uyar.
  useEffect(() => {
    setPageState(currentPage)
  }, [currentPage])

  useEffect(() => {
    setSearch(currentSearch)
    setSearchInput(currentSearch)
  }, [currentSearch])

  const updateSearchParams = useCallback((overrides: { cat?: string; page?: number; search?: string }) => {
    const params = new URLSearchParams()
    const cat = overrides.cat ?? currentCategory
    const slug = apiKeyToUrlSlug(cat, lang)
    // Kategori sayfasındayken kategori yolda taşınır, parametreye kopyalanmaz.
    if (slug && !categoryFromRoute) params.set(catParam, slug)
    const pg = overrides.page ?? 1
    if (pg > 1) params.set(pageParam, String(pg))
    const s = overrides.search ?? search
    if (s) params.set(searchParam, s)
    const qs = params.toString()
    // Durum önce güncellenir; adres çubuğu yalnızca yansıtır.
    setPageState(pg)
    setSearch(s)
    // Use window.history to update URL without full navigation
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname)
  }, [currentCategory, search, lang, catParam, pageParam, searchParam, categoryFromRoute])

  useEffect(() => {
    if (seedConsumed.current) {
      seedConsumed.current = false
      return
    }
    setLoading(true)
    fetchAllNews(page, 12, currentCategory || undefined, lang, search || undefined).then(result => {
      if (result) {
        setNews(result.data)
        setTotalPages(result.totalPages)
        setTotalCount(result.totalCount)
      } else {
        setNews([])
        setTotalPages(1)
        setTotalCount(0)
      }
      setLoading(false)
    })
  }, [currentCategory, page, search, lang])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      updateSearchParams({ search: value, page: 1 })
    }, 400)
  }

  const clearSearch = () => {
    setSearchInput('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    updateSearchParams({ search: '', page: 1 })
  }

  const setPage = (page: number) => {
    updateSearchParams({ page })
    setTimeout(() => {
      navRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const activeCat = categoryKeys.find(c => c.key === currentCategory)
  const activeCatLabel = activeCat ? t(activeCat.i18n, lang) : t('news.all', lang)

  const pageNumbers: number[] = []
  const maxVisible = typeof window !== 'undefined' && window.innerWidth < 640 ? 3 : 5
  let start = Math.max(1, page - Math.floor(maxVisible / 2))
  const end = Math.min(totalPages, start + maxVisible - 1)
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
  for (let i = start; i <= end; i++) pageNumbers.push(i)

  return (
    <>
      {/* Breadcrumb */}
      <nav ref={navRef} className="flex items-center gap-1.5 text-[12px] mb-5 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="no-underline hover:underline shrink-0" style={{ color: 'var(--text-muted)' }}>
          {t('nav.home', lang)}
        </Link>
        <span className="shrink-0">/</span>
        {currentCategory ? (
          <Link href="/news" className="no-underline hover:underline shrink-0" style={{ color: 'var(--text-muted)' }}>
            {t('news.title', lang)}
          </Link>
        ) : (
          <span className="shrink-0" style={{ color: 'var(--text-main)' }}>{t('news.title', lang)}</span>
        )}
        {currentCategory && (
          <>
            <span className="shrink-0">/</span>
            <span className="truncate" style={{ color: categoryColorMap[currentCategory] ?? 'var(--text-main)' }}>
              {activeCatLabel}
            </span>
          </>
        )}
      </nav>

      {/* Search */}
      <div
        className="relative mb-4 rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder={t('news.searchPlaceholder', lang)}
          className="w-full py-2.5 pl-10 pr-9 text-[13px] bg-transparent outline-none"
          style={{ color: 'var(--text-main)' }}
        />
        {searchInput && (
          <button
            onClick={clearSearch}
            aria-label={lang === 'en' ? 'Clear search' : 'Aramayı temizle'}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full transition-colors"
            style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-muted)' }}
            title={t('news.clearSearch', lang)}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {search && !loading && (
        <p className="text-[12px] mb-3" style={{ color: 'var(--text-muted)' }}>
          {t('news.searchResults', lang).replace('{count}', String(totalCount))}
        </p>
      )}

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categoryKeys.map(cat => {
          const isActive = currentCategory === cat.key
          const color = cat.color ?? 'var(--text-muted)'
          const style = {
            backgroundColor: isActive ? (cat.color ? cat.color + '18' : 'var(--accent-color)' + '18') : 'transparent',
            borderColor: isActive ? color : 'var(--border-color)',
            color: isActive ? color : 'var(--text-muted)',
          }
          const cls = 'text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-all duration-200 no-underline inline-block'
          // "Tümü" kök listeye, diğerleri kendi kategori sayfasına GERÇEK bağlantıdır.
          return cat.key === '' ? (
            <Link key="all" href="/news" className={cls} style={style} aria-current={isActive ? 'page' : undefined}>
              {t(cat.i18n, lang)}
            </Link>
          ) : (
            <Link
              key={cat.key}
              href={{ pathname: '/news/category/[category]', params: { category: categorySlugFor(cat, lang) } }}
              className={cls}
              style={style}
              aria-current={isActive ? 'page' : undefined}
            >
              {t(cat.i18n, lang)}
            </Link>
          )
        })}
      </div>

      {/* News Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : news.length === 0 ? (
        <div
          className="text-center py-16 rounded-xl border"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <div className="text-4xl mb-3">📰</div>
          <h3 className="text-[15px] font-bold mb-1" style={{ color: 'var(--text-main)' }}>
            {t('news.notFound', lang)}
          </h3>
          <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
            {t('news.emptyCategory', lang)}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {news.map(item => (
            <NewsCard key={item.id} item={item} lang={lang} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-8 flex-wrap">
          {/* Prev */}
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="h-8 px-3 text-[12px] font-medium rounded-lg border transition-colors disabled:opacity-30 whitespace-nowrap shrink-0"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-main)', backgroundColor: 'var(--bg-card)' }}
          >
            <span className="hidden sm:inline">{t('news.prev', lang)}</span>
            <span className="sm:hidden">←</span>
          </button>

          {start > 1 && (
            <>
              <button
                onClick={() => setPage(1)}
                className="w-8 h-8 text-[12px] font-medium rounded-lg border transition-colors shrink-0"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)', backgroundColor: 'var(--bg-card)' }}
              >
                1
              </button>
              {start > 2 && (
                <span className="text-[12px] px-0.5 shrink-0" style={{ color: 'var(--text-muted)' }}>…</span>
              )}
            </>
          )}

          {pageNumbers.map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className="w-8 h-8 text-[12px] font-medium rounded-lg border transition-colors shrink-0"
              style={{
                borderColor: p === page ? 'var(--accent-color)' : 'var(--border-color)',
                color: p === page ? 'var(--accent-color)' : 'var(--text-muted)',
                backgroundColor: p === page ? 'var(--accent-color)' + '12' : 'var(--bg-card)',
                fontWeight: p === page ? 700 : 500,
              }}
            >
              {p}
            </button>
          ))}

          {end < totalPages && (
            <>
              {end < totalPages - 1 && (
                <span className="text-[12px] px-0.5 shrink-0" style={{ color: 'var(--text-muted)' }}>…</span>
              )}
              <button
                onClick={() => setPage(totalPages)}
                className="w-8 h-8 text-[12px] font-medium rounded-lg border transition-colors shrink-0"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)', backgroundColor: 'var(--bg-card)' }}
              >
                {totalPages}
              </button>
            </>
          )}

          {/* Next */}
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="h-8 px-3 text-[12px] font-medium rounded-lg border transition-colors disabled:opacity-30 whitespace-nowrap shrink-0"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-main)', backgroundColor: 'var(--bg-card)' }}
          >
            <span className="hidden sm:inline">{t('news.next', lang)}</span>
            <span className="sm:hidden">→</span>
          </button>
        </div>
      )}
    </>
  )
}
